import React, { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import CategoryFilter from '../components/CategoryFilter';
import LocationSearch from '../components/LocationSearch';
import StartupGrid from '../components/StartupGrid';
import StartupMap from '../components/StartupMap';
import AddStartupButton from '../components/AddStartupButton';
import { getStartups, getStartupsCount } from '../services/api';
import { Button } from '../components/ui/button';
import { Loader2, Map, Grid } from 'lucide-react';

const HomePage = () => {
  const [sortBy, setSortBy] = useState('latest');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [locationFilters, setLocationFilters] = useState({
    continent: 'All',
    country: '',
    city: '',
    search: ''
  });
  const [startups, setStartups] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [visibleCount, setVisibleCount] = useState(24);
  const [showMap, setShowMap] = useState(true);

  const fetchStartups = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        category: selectedCategory,
        sort: sortBy,
        limit: 100,
        ...locationFilters
      };
      
      const [data, count] = await Promise.all([
        getStartups(params),
        getStartupsCount({
          category: selectedCategory,
          continent: locationFilters.continent,
          country: locationFilters.country,
          city: locationFilters.city
        })
      ]);
      setStartups(data);
      setTotalCount(count);
      setVisibleCount(24);
    } catch (error) {
      console.error('Failed to fetch startups:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, sortBy, locationFilters]);

  useEffect(() => {
    fetchStartups();
  }, [fetchStartups]);

  const handleShowMore = () => {
    setLoadingMore(true);
    setTimeout(() => {
      setVisibleCount(prev => prev + 12);
      setLoadingMore(false);
    }, 300);
  };

  const handleStartupAdded = () => {
    fetchStartups();
  };

  const handleLocationFilterChange = (newFilters) => {
    setLocationFilters(newFilters);
  };

  const showingCount = Math.min(visibleCount, startups.length);

  return (
    <div className="min-h-screen bg-white">
      <Header sortBy={sortBy} setSortBy={setSortBy} />
      <CategoryFilter 
        selectedCategory={selectedCategory} 
        setSelectedCategory={setSelectedCategory} 
      />
      <LocationSearch 
        filters={locationFilters}
        onFilterChange={handleLocationFilterChange}
      />

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : startups.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No startups found matching your filters.</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => {
                setSelectedCategory('all');
                setLocationFilters({ continent: 'All', country: '', city: '', search: '' });
              }}
            >
              Clear all filters
            </Button>
          </div>
        ) : (
          <>
            {/* Map Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Map className="w-5 h-5 text-emerald-600" />
                  Explore by Location
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowMap(!showMap)}
                  className="text-gray-600"
                >
                  {showMap ? (
                    <>
                      <Grid className="w-4 h-4 mr-2" />
                      Hide Map
                    </>
                  ) : (
                    <>
                      <Map className="w-4 h-4 mr-2" />
                      Show Map
                    </>
                  )}
                </Button>
              </div>
              
              {showMap && <StartupMap startups={startups} />}
            </div>

            {/* Grid Section */}
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Grid className="w-5 h-5 text-emerald-600" />
                All Startups
              </h2>
            </div>
            
            <StartupGrid 
              startups={startups} 
              visibleCount={visibleCount}
              onVoteChange={fetchStartups}
            />

            {/* Show More / Count */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500 mb-4">
                Showing {showingCount} of {totalCount} startups
              </p>
              {visibleCount < startups.length && (
                <Button
                  onClick={handleShowMore}
                  disabled={loadingMore}
                  variant="outline"
                  className="px-8 py-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50 text-gray-700"
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Show More
                </Button>
              )}
            </div>
          </>
        )}
      </main>

      <AddStartupButton onStartupAdded={handleStartupAdded} />
    </div>
  );
};

export default HomePage;
